import { useCallback, useContext, useMemo, useState } from 'react';
import styled from '@emotion/styled';
import type { KolSearchForm, KolSearchResult } from '@trinity/chat';
import { ChatActionType, KolSearchStatusEnum } from '@trinity/chat';
import { useSelectedUserTerritoryId } from '@trinity/chat-components/components/Chat.Filters/hooks/useSelectedUserTerritoryId';
import {
  DispatchKolSearchFormContext,
  KolSearchFormContext,
  KolSearchStatusContext,
  KolSearchSubmitContext,
  SubmitActionContext,
} from './context';
import { KolSearchFormMessage } from './KolSearch.Form';
import { MessageContainer } from './Message.System.Wrapper';

type Props = {
  disabled?: boolean;
};

export const HcpSearchResponse = ({ disabled = false }: Props) => {
  const submitAction = useContext(SubmitActionContext);
  const territoryId = useSelectedUserTerritoryId();
  const [formState, setFormState] = useState<Partial<KolSearchForm>>({
    kolId: null,
    name: '',
  });
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleFormUpdated = useCallback((form: Partial<KolSearchForm>) => {
    setFormState((prev) => ({ ...prev, ...form }));
    setErrorMessage(null);
  }, []);

  const handleAutocompleteSelection = useCallback(
    (item: KolSearchResult) => {
      if (disabled) {
        return;
      }

      submitAction(
        {
          type: ChatActionType.SelectHcpSubject,
          payload: {
            hcpId: item.id,
            hcpName: item.name,
          },
        },
        `Show me the profile for ${item.name}.`
      );
    },
    [disabled, submitAction]
  );

  const handleLocationSelection = useCallback(
    (location: NonNullable<KolSearchForm['location']>) => {
      if (disabled) {
        return;
      }

      const trimmedName = formState.name?.trim();

      submitAction(
        {
          type: ChatActionType.SearchHcps,
          payload: {
            ...(trimmedName ? { name: trimmedName } : {}),
            territoryId,
            location: {
              id: location.id,
              latitude: location.latitude,
              longitude: location.longitude,
              name: location.name,
            },
          },
        },
        location.name
          ? `Search for HCPs near ${location.name}.`
          : 'Search for HCPs near the selected location.'
      );
    },
    [disabled, formState.name, submitAction]
  );

  const submitQuery = useMemo(() => {
    const trimmedName = formState.name?.trim();
    if (trimmedName) {
      return `Search for HCPs matching ${trimmedName}.`;
    }

    if (formState.location?.name) {
      return `Search for HCPs near ${formState.location.name}.`;
    }

    return 'Search for HCPs.';
  }, [formState.location?.name, formState.name]);

  const handleSubmit = useCallback(() => {
    if (disabled) {
      return;
    }

    const hcpId = formState.kolId;
    const hcpName = formState.name?.trim();

    if (typeof hcpId === 'number' && hcpName) {
      submitAction(
        {
          type: ChatActionType.SelectHcpSubject,
          payload: {
            hcpId,
            hcpName,
          },
        },
        `Show me the profile for ${hcpName}.`
      );
      return;
    }

    const hasName = !!hcpName;
    const location = formState.location;
    const hasLocation = !!location;

    if (!hasName && !hasLocation) {
      setErrorMessage('Please select an HCP, enter a name, or provide a location before submitting.');
      return;
    }

    submitAction(
      {
        type: ChatActionType.SearchHcps,
        payload: {
          ...(hasName ? { name: hcpName } : {}),
          territoryId,
          ...(hasLocation
            ? {
                location: {
                  id: location.id,
                  latitude: location.latitude,
                  longitude: location.longitude,
                  name: location.name,
                },
              }
            : {}),
        },
      },
      submitQuery
    );
  }, [disabled, formState.kolId, formState.location, formState.name, submitAction, submitQuery, territoryId]);

  return (
    <MessageContainer>
      <KolSearchFormContext.Provider value={formState}>
        <DispatchKolSearchFormContext.Provider value={handleFormUpdated}>
          <KolSearchStatusContext.Provider value={KolSearchStatusEnum.FormEntry}>
            <KolSearchSubmitContext.Provider value={handleSubmit}>
              <KolSearchFormMessage
                disabled={disabled}
                onAutocompleteSelect={handleAutocompleteSelection}
                onLocationSelect={handleLocationSelection}
                showSearchButton={false}
              />
              {errorMessage ? <ErrorMessage>{errorMessage}</ErrorMessage> : null}
            </KolSearchSubmitContext.Provider>
          </KolSearchStatusContext.Provider>
        </DispatchKolSearchFormContext.Provider>
      </KolSearchFormContext.Provider>
    </MessageContainer>
  );
};

const ErrorMessage = styled.div(({ theme }) => ({
  marginTop: 10,
  color: theme.palette.error.main,
  fontFamily: theme.fonts.regular,
}));
